import { OrderStatus } from '../../../proto/generated/order'

export const ORDER_PROPOSAL_ARTIFACT_TYPE = 'order_proposal'
/** Legacy artifact type from the preview-table era. */
export const ORDER_PREVIEW_ARTIFACT_TYPE = 'order_preview'
export const LIVE_ORDER_ACKNOWLEDGEMENT = 'LIVE_ORDER_ACKNOWLEDGED'

export type OrderPreviewInstrument = {
    label: string
    exchange: string
    code: string
}

export type OrderPreviewAccount = {
    label: string
    mode: string
}

export type OrderPreviewOperation = 'CREATE' | 'MODIFY' | 'CANCEL'

export type OrderPreviewSnapshot = {
    quantity: number
    orderType: string
    side: string
    limitPrice?: number | null
    triggerPrice?: number | null
    status?: string | null
}

export type ModifySnapshotDisplay = {
    quantity: number
    orderTypeLabel: string
    triggerPriceLabel?: string
    limitPriceLabel?: string
}

export type OrderPreviewLegRole = 'ENTRY' | 'PROTECTIVE_STOP' | 'TARGET'

export type OrderPreviewLeg = {
    role: OrderPreviewLegRole
    orderType: string
    side: string
    quantity: number
    limitPrice?: number | null
    triggerPrice?: number | null
    clientOrderId: string
    orderId?: string | null
    brokerOrderId?: string | null
    orderStatus?: string | null
    cancelDisposition?: string | null
    status?: string | null
}

export type CancelBracketLegDisplay = {
    title: string
    side: string
    orderTypeLabel: string
    limitPriceLabel?: string
    triggerPriceLabel?: string
    willCancel: boolean
    statusLabel?: string
}

/** Resolved F&O contract parts; absent for cash instruments. */
export type OrderPreviewContract = {
    label: string
    underlying?: string | null
    expiry?: string | null
    weeklyExpiry?: boolean | null
    strike?: number | null
    optionRight?: string | null
    instrument?: string | null
}

/** Server echo of a conditional entry (order held until the condition fires; today's session only). */
export type OrderPreviewCondition = {
    /** RULES: indicator and/or combined conditions; only the summary is shown. */
    kind: 'PRICE' | 'TIME' | 'RULES'
    operator?: string | null
    priceRupees?: number | null
    time?: string | null
    summary: string
    /** RULES: each indicator's full settings, defaults included. */
    settings?: string[] | null
    /** CONTRACT (the order's own instrument) or UNDERLYING (the index or stock it is written on). */
    watch?: string | null
    /** Name of the watched instrument, e.g. "Nifty 50". */
    watchedLabel?: string | null
}

/** The standalone position a square-off proposal closes (quantities before this order). */
export type OrderPreviewPositionExit = {
    positionSide: 'LONG' | 'SHORT'
    netQuantity: number
    exitQuantity: number
    product: string
    averagePrice?: number | null
}

/** Stop / target attached to an existing position. */
export type OrderPreviewPositionProtection = {
    positionSide: 'LONG' | 'SHORT'
    quantity: number
    product: string
    stopTriggerPrice?: number | null
    targetPrice?: number | null
    averagePrice?: number | null
    replacesExisting: boolean
    /** Set when the proposal also arms a conditional exit. */
    condition?: OrderPreviewCondition | null
}

/** One position being squared off by an exit-all proposal. */
export type OrderPreviewExitAllLeg = {
    side: string
    instrumentLabel: string
    exchange: string
    securityCode: string
    quantity: number
    positionSide: 'LONG' | 'SHORT'
    product: string
    averagePrice?: number | null
    clientOrderId: string
}

export type OrderPreviewExitAll = {
    legs: OrderPreviewExitAllLeg[]
    /** Set when the user narrowed the request, e.g. "exit all intraday positions". */
    product?: string | null
}

/** One leg of a multi-leg basket proposal. */
/** A named strategy proposal: which template, on what, and why each strike was picked. */
export type OrderPreviewStrategy = {
    name: string
    underlying: string
    expiries: string[]
    /** Lines up with basketLegs. */
    legReasons: string[]
    riskNote?: string | null
}

export type OrderPreviewBasketLeg = {
    side: string
    instrumentLabel: string
    exchange: string
    securityCode: string
    quantity: number
    lots?: number | null
    orderType: string
    limitPrice?: number | null
    clientOrderId: string
}

export type OrderPreview = {
    proposalId: string
    status: string
    operation: OrderPreviewOperation
    side: string
    quantity: number
    orderType: string
    /** Display limit price in rupees for LIMIT and SL orders. */
    limitPrice?: number | null
    /** Display trigger price in rupees for SL-M and SL orders. */
    triggerPrice?: number | null
    product?: string | null
    validity?: string | null
    lotSize?: number | null
    lots?: number | null
    icebergLegs?: number | null
    disclosedQuantity?: number | null
    condition?: OrderPreviewCondition | null
    positionExit?: OrderPreviewPositionExit | null
    positionProtection?: OrderPreviewPositionProtection | null
    basketLegs?: OrderPreviewBasketLeg[] | null
    strategy?: OrderPreviewStrategy | null
    exitAll?: OrderPreviewExitAll | null
    /** CANCEL of a conditional basket's gate: the condition and every held leg go together. */
    cancelsConditionalBasket?: boolean
    requiredMarginRupees?: number | null
    contractLabel?: string | null
    contract?: OrderPreviewContract | null
    instrument: OrderPreviewInstrument
    account: OrderPreviewAccount
    expiresAt: string
    confirmationToken: string
    messages: string[]
    orderId?: string | null
    brokerOrderId?: string | null
    orderStatus?: string | null
    executionErrorMessage?: string | null
    clientOrderId?: string | null
    targetOrderId?: string | null
    currentSnapshot?: OrderPreviewSnapshot | null
    proposedSnapshot?: OrderPreviewSnapshot | null
    legs?: OrderPreviewLeg[] | null
    /** Bracket leg being modified (TARGET, PROTECTIVE_STOP). */
    modifyLegRole?: string | null
}

export type ConfirmOrderProposalRequest = {
    confirmationToken: string
    liveAcknowledgement?: typeof LIVE_ORDER_ACKNOWLEDGEMENT
}

export type OrderPreviewIssue = 'stale' | 'expired' | 'error'

/** Minimal OMS order slice used to link AI proposals to Redux `ordersHistory`. */
export type LinkedOmsOrder = {
    clientOrderId: string
    orderId?: string
    status?: number
    secondaryStatus?: number
    filledQuantity?: number
    message?: string
}

export type OrderPreviewCardModel = {
    operation: OrderPreviewOperation
    confirmButtonLabel: string
    side: string
    instrumentCode: string
    triggerPriceLabel?: string
    limitPriceLabel?: string
    targetOrderIdShort?: string
    modifyCurrent?: ModifySnapshotDisplay
    modifyProposed?: ModifySnapshotDisplay
    modifyLegLabel?: string
    cancelOrderDetails?: ModifySnapshotDisplay
    cancelBracketLegs?: CancelBracketLegDisplay[]
    currentSnapshotStatus?: string
    accountLabel: string
    accountMode: string
    omsStatus?: number
    filledQuantity?: number
    brokerMessage?: string
    canConfirm: boolean
    orderId?: string
    /** True once the card should follow OMS state instead of proposal expiry. */
    trackingOms: boolean
    isProtectiveStopBracket?: boolean
    reviewProtectiveStopHint?: string
}

type ConversationArtifact = {
    kind: 'custom'
    type: string
    data?: unknown
}

function nonEmptyString(value: unknown): string | null {
    return typeof value === 'string' && value.trim() ? value : null
}

function parseOrderPreviewCondition(value: unknown): OrderPreviewCondition | null {
    if (!value || typeof value !== 'object') return null
    const raw = value as Record<string, unknown>
    const kind = raw.kind === 'PRICE' || raw.kind === 'TIME' || raw.kind === 'RULES' ? raw.kind : null
    const summary = nonEmptyString(raw.summary)
    if (!kind || !summary) return null
    return {
        kind,
        operator: nonEmptyString(raw.operator),
        priceRupees: parsePriceRupees(raw.priceRupees),
        time: nonEmptyString(raw.time),
        summary,
        watch: nonEmptyString(raw.watch),
        watchedLabel: nonEmptyString(raw.watchedLabel),
        settings: Array.isArray(raw.settings)
            ? raw.settings.filter((line): line is string => typeof line === 'string' && line.trim() !== '')
            : null,
    }
}

function parseOrderPreviewPositionExit(value: unknown): OrderPreviewPositionExit | null {
    if (!value || typeof value !== 'object') return null
    const raw = value as Record<string, unknown>
    const positionSide = raw.positionSide === 'LONG' || raw.positionSide === 'SHORT' ? raw.positionSide : null
    if (!positionSide || typeof raw.netQuantity !== 'number' || typeof raw.exitQuantity !== 'number') return null
    return {
        positionSide,
        netQuantity: raw.netQuantity,
        exitQuantity: raw.exitQuantity,
        product: nonEmptyString(raw.product) ?? '',
        averagePrice: parsePriceRupees(raw.averagePrice),
    }
}

function parseOrderPreviewPositionProtection(value: unknown): OrderPreviewPositionProtection | null {
    if (!value || typeof value !== 'object') return null
    const raw = value as Record<string, unknown>
    const positionSide = raw.positionSide === 'LONG' || raw.positionSide === 'SHORT' ? raw.positionSide : null
    if (!positionSide || typeof raw.quantity !== 'number') return null
    return {
        positionSide,
        quantity: raw.quantity,
        product: nonEmptyString(raw.product) ?? '',
        stopTriggerPrice: parsePriceRupees(raw.stopTriggerPrice),
        targetPrice: parsePriceRupees(raw.targetPrice),
        averagePrice: parsePriceRupees(raw.averagePrice),
        replacesExisting: raw.replacesExisting === true,
        condition: parseOrderPreviewCondition(raw.condition),
    }
}

function parseOrderPreviewExitAll(value: unknown): OrderPreviewExitAll | null {
    if (!value || typeof value !== 'object') return null
    const raw = value as Record<string, unknown>
    if (!Array.isArray(raw.legs)) return null
    const legs: OrderPreviewExitAllLeg[] = []
    for (const entry of raw.legs) {
        if (!entry || typeof entry !== 'object') continue
        const leg = entry as Record<string, unknown>
        const side = nonEmptyString(leg.side)
        const instrumentLabel = nonEmptyString(leg.instrumentLabel)
        const clientOrderId = nonEmptyString(leg.clientOrderId)
        const positionSide = leg.positionSide === 'LONG' || leg.positionSide === 'SHORT' ? leg.positionSide : null
        if (!side || !instrumentLabel || !clientOrderId || !positionSide || typeof leg.quantity !== 'number') continue
        legs.push({
            side,
            instrumentLabel,
            exchange: nonEmptyString(leg.exchange) ?? '',
            securityCode: nonEmptyString(leg.securityCode) ?? '',
            quantity: leg.quantity,
            positionSide,
            product: nonEmptyString(leg.product) ?? '',
            averagePrice: parsePriceRupees(leg.averagePrice),
            clientOrderId,
        })
    }
    if (legs.length === 0) return null
    return { legs, product: nonEmptyString(raw.product) }
}

/** "SELL 10 RELIANCE · closes LONG 10 · Intraday · avg ₹1,400". */
export function formatExitAllLegLine(leg: OrderPreviewExitAllLeg): string {
    const parts = [
        `${leg.side.toUpperCase()} ${leg.quantity} ${leg.instrumentLabel}`,
        `closes ${leg.positionSide} ${leg.quantity}`,
    ]
    if (leg.product) parts.push(formatProductLabel(leg.product))
    if (leg.averagePrice != null) parts.push(`avg ${formatOrderPriceRupees(leg.averagePrice)}`)
    return parts.join(' · ')
}

function parseOrderPreviewStrategy(value: unknown): OrderPreviewStrategy | null {
    if (!value || typeof value !== 'object') return null
    const raw = value as Record<string, unknown>
    const name = nonEmptyString(raw.name)
    if (!name) return null
    const strings = (list: unknown): string[] =>
        Array.isArray(list) ? list.filter((item): item is string => typeof item === 'string') : []
    return {
        name,
        underlying: nonEmptyString(raw.underlying) ?? '',
        expiries: strings(raw.expiries),
        legReasons: strings(raw.legReasons),
        riskNote: nonEmptyString(raw.riskNote),
    }
}

function parseOrderPreviewBasketLegs(value: unknown): OrderPreviewBasketLeg[] | null {
    if (!Array.isArray(value)) return null
    const legs: OrderPreviewBasketLeg[] = []
    for (const entry of value) {
        if (!entry || typeof entry !== 'object') continue
        const raw = entry as Record<string, unknown>
        const side = nonEmptyString(raw.side)
        const instrumentLabel = nonEmptyString(raw.instrumentLabel)
        const clientOrderId = nonEmptyString(raw.clientOrderId)
        if (!side || !instrumentLabel || !clientOrderId || typeof raw.quantity !== 'number') continue
        legs.push({
            side,
            instrumentLabel,
            exchange: nonEmptyString(raw.exchange) ?? '',
            securityCode: nonEmptyString(raw.securityCode) ?? '',
            quantity: raw.quantity,
            lots: typeof raw.lots === 'number' ? raw.lots : null,
            orderType: nonEmptyString(raw.orderType) ?? 'MARKET',
            limitPrice: parsePriceRupees(raw.limitPrice),
            clientOrderId,
        })
    }
    return legs.length > 0 ? legs : null
}

/** "BUY 1 lot NIFTY 24000 CE · at market". */
export function formatBasketLegLine(leg: OrderPreviewBasketLeg): string {
    const size = leg.lots != null ? `${leg.lots} lot${leg.lots === 1 ? '' : 's'}` : `${leg.quantity}`
    const price =
        leg.limitPrice != null ? `limit ${formatOrderPriceRupees(leg.limitPrice)}` : 'at market'
    return `${leg.side.toUpperCase()} ${size} ${leg.instrumentLabel} · ${price}`
}

function parsePriceRupees(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
        return value
    }
    if (typeof value === 'string' && value.trim()) {
        const parsed = Number(value)
        if (Number.isFinite(parsed) && parsed > 0) {
            return parsed
        }
    }
    return null
}

export function formatOrderPriceRupees(price: number): string {
    const formatted = price.toLocaleString('en-IN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    })
    return `₹${formatted}`
}

function normalizedOrderTypeKey(orderType: string): string {
    return orderType.trim().toUpperCase().replace(/-/g, '_')
}

export function formatProductLabel(product: string): string {
    const key = product.trim().toUpperCase()
    switch (key) {
        case 'CNC':
            return 'Delivery'
        case 'INTRADAY':
            return 'Intraday'
        case 'MARGIN':
            return 'Overnight'
        case 'MTF':
            return 'MTF'
        default:
            return product
    }
}

/** "Protects LONG 10 · Intraday · avg ₹1,400". */
export function formatPositionProtectionLine(protection: OrderPreviewPositionProtection): string {
    const parts = [`${protection.positionSide} ${protection.quantity}`]
    if (protection.product) parts.push(formatProductLabel(protection.product))
    if (protection.averagePrice != null) parts.push(`avg ${formatOrderPriceRupees(protection.averagePrice)}`)
    return parts.join(' · ')
}

/** "LONG 10 · Intraday · avg ₹1,400" or "5 of LONG 10 · …" for a partial exit. */
export function formatPositionExitLine(exit: OrderPreviewPositionExit): string {
    const held = `${exit.positionSide} ${exit.netQuantity}`
    const scope = exit.exitQuantity < exit.netQuantity ? `${exit.exitQuantity} of ${held}` : held
    const parts = [scope]
    if (exit.product) parts.push(formatProductLabel(exit.product))
    if (exit.averagePrice != null) parts.push(`avg ${formatOrderPriceRupees(exit.averagePrice)}`)
    return parts.join(' · ')
}

export function formatValidityLabel(validity: string): string {
    const key = validity.trim().toUpperCase()
    switch (key) {
        case 'DAY':
            return 'Day'
        case 'IOC':
            return 'IOC (Immediate or Cancel)'
        default:
            return validity
    }
}

export function formatOrderTypeLabel(orderType: string): string {
    switch (normalizedOrderTypeKey(orderType)) {
        case 'SL_M':
            return 'SL-M'
        case 'MARKET':
            return 'MARKET'
        case 'LIMIT':
            return 'LIMIT'
        case 'SL':
            return 'SL'
        default:
            return orderType
    }
}

export function isLimitOrderPreview(preview: OrderPreview): boolean {
    return normalizedOrderTypeKey(preview.orderType) === 'LIMIT'
}

export function isStopMarketOrderPreview(preview: OrderPreview): boolean {
    return normalizedOrderTypeKey(preview.orderType) === 'SL_M'
}

export function isStopLimitOrderPreview(preview: OrderPreview): boolean {
    return normalizedOrderTypeKey(preview.orderType) === 'SL'
}

export function isModifyOrderPreview(preview: OrderPreview): boolean {
    return preview.operation === 'MODIFY'
}

export function isCancelOrderPreview(preview: OrderPreview): boolean {
    return preview.operation === 'CANCEL'
}

export function isProtectiveStopBracketPreview(preview: OrderPreview): boolean {
    if (preview.legs?.length) {
        return preview.legs.some((leg) => leg.role === 'PROTECTIVE_STOP')
    }
    return preview.orderType.toUpperCase().includes('+SL-M')
}

export function isThreeLegBracketPreview(preview: OrderPreview): boolean {
    if (preview.legs?.length) {
        return preview.legs.some((leg) => leg.role === 'TARGET')
    }
    return preview.orderType.toUpperCase().includes('+TARGET')
}

function parseOrderPreviewLegRole(value: unknown): OrderPreviewLegRole | null {
    const role = nonEmptyString(value)?.toUpperCase()
    if (role === 'ENTRY') return 'ENTRY'
    if (role === 'PROTECTIVE_STOP') return 'PROTECTIVE_STOP'
    if (role === 'TARGET') return 'TARGET'
    return null
}

function parseOrderPreviewLegs(value: unknown, lenient = false): OrderPreviewLeg[] | null {
    if (!Array.isArray(value) || value.length === 0) return null
    const legs: OrderPreviewLeg[] = []
    for (const raw of value) {
        if (!raw || typeof raw !== 'object') return null
        const leg = raw as Record<string, unknown>
        const role = parseOrderPreviewLegRole(leg.role)
        const clientOrderId = nonEmptyString(leg.clientOrderId)
        const orderType = nonEmptyString(leg.orderType)
        const side = nonEmptyString(leg.side)
        if (!role || !clientOrderId) {
            return null
        }
        if (!lenient && (!orderType || !side || !Number.isInteger(leg.quantity))) {
            return null
        }
        legs.push({
            role,
            orderType: orderType ?? '',
            side: side ?? '',
            quantity: Number.isInteger(leg.quantity) ? (leg.quantity as number) : 0,
            limitPrice: parsePriceRupees(leg.limitPrice),
            triggerPrice: parsePriceRupees(leg.triggerPrice),
            clientOrderId,
            orderId: nonEmptyString(leg.orderId),
            brokerOrderId: nonEmptyString(leg.brokerOrderId),
            orderStatus: nonEmptyString(leg.orderStatus),
            cancelDisposition: nonEmptyString(leg.cancelDisposition),
            status: nonEmptyString(leg.status),
        })
    }
    return legs
}

function cancelLegTitle(role: OrderPreviewLegRole): string {
    if (role === 'ENTRY') return 'Entry'
    if (role === 'PROTECTIVE_STOP') return 'Protective stop'
    return 'Target'
}

function cancelBracketLegDisplayFromLeg(leg: OrderPreviewLeg): CancelBracketLegDisplay {
    const willCancel = leg.cancelDisposition?.toUpperCase() === 'WILL_CANCEL'
    return {
        title: cancelLegTitle(leg.role),
        side: leg.side.toUpperCase(),
        orderTypeLabel: formatOrderTypeLabel(leg.orderType),
        ...(leg.limitPrice != null ? { limitPriceLabel: formatOrderPriceRupees(leg.limitPrice) } : {}),
        ...(leg.triggerPrice != null ? { triggerPriceLabel: formatOrderPriceRupees(leg.triggerPrice) } : {}),
        willCancel,
        statusLabel: leg.status ?? leg.orderStatus ?? undefined,
    }
}

function mergeLegExecution(
    previewLegs: OrderPreviewLeg[] | null | undefined,
    executionLegs: OrderPreviewLeg[] | null | undefined,
): OrderPreviewLeg[] | null | undefined {
    if (!previewLegs?.length) return executionLegs ?? previewLegs
    if (!executionLegs?.length) return previewLegs
    return previewLegs.map((leg) => {
        const exec = executionLegs.find(
            (candidate) =>
                candidate.clientOrderId === leg.clientOrderId ||
                (candidate.role === leg.role && candidate.clientOrderId === leg.clientOrderId),
        )
        if (!exec) return leg
        return {
            ...leg,
            orderId: exec.orderId ?? leg.orderId,
            brokerOrderId: exec.brokerOrderId ?? leg.brokerOrderId,
            orderStatus: exec.orderStatus ?? leg.orderStatus,
        }
    })
}

export function parseOrderPreviewOperation(value: unknown): OrderPreviewOperation {
    if (typeof value !== 'string' || !value.trim()) {
        return 'CREATE'
    }
    const key = value.trim().toUpperCase()
    if (key === 'MODIFY') return 'MODIFY'
    if (key === 'CANCEL') return 'CANCEL'
    return 'CREATE'
}

function parseOrderPreviewSnapshot(value: unknown): OrderPreviewSnapshot | null {
    if (!value || typeof value !== 'object') return null
    const data = value as Record<string, unknown>
    const side = nonEmptyString(data.side)
    const orderType = nonEmptyString(data.orderType)
    if (!side || !orderType || !Number.isInteger(data.quantity)) {
        return null
    }
    return {
        quantity: data.quantity as number,
        orderType,
        side,
        limitPrice: parsePriceRupees(data.limitPrice),
        triggerPrice: parsePriceRupees(data.triggerPrice),
        status: nonEmptyString(data.status),
    }
}

export function shortOrderId(orderId: string): string {
    const trimmed = orderId.trim()
    if (trimmed.length <= 14) return trimmed
    return `…${trimmed.slice(-10)}`
}

export function snapshotToModifyDisplay(snapshot: OrderPreviewSnapshot): ModifySnapshotDisplay {
    const orderTypeKey = normalizedOrderTypeKey(snapshot.orderType)
    return {
        quantity: snapshot.quantity,
        orderTypeLabel: formatOrderTypeLabel(snapshot.orderType),
        ...((orderTypeKey === 'SL_M' || orderTypeKey === 'SL') && snapshot.triggerPrice
            ? { triggerPriceLabel: formatOrderPriceRupees(snapshot.triggerPrice) }
            : {}),
        ...((orderTypeKey === 'LIMIT' || orderTypeKey === 'SL') && snapshot.limitPrice
            ? { limitPriceLabel: formatOrderPriceRupees(snapshot.limitPrice) }
            : {}),
    }
}

export function parseOrderPreview(value: unknown): OrderPreview | null {
    if (!value || typeof value !== 'object') return null
    const data = value as Record<string, unknown>
    const instrument = data.instrument as Record<string, unknown> | undefined
    const account = data.account as Record<string, unknown> | undefined
    const proposalId = nonEmptyString(data.proposalId) ?? nonEmptyString(data.previewId)
    const confirmationToken = nonEmptyString(data.confirmationToken)
    const side = nonEmptyString(data.side)
    const orderType = nonEmptyString(data.orderType)
    const expiresAt = nonEmptyString(data.expiresAt)
    const instrumentLabel = nonEmptyString(instrument?.label)
    const exchange = nonEmptyString(instrument?.exchange)
    const code = nonEmptyString(instrument?.code)
    const accountLabel = nonEmptyString(account?.label)
    const accountMode = nonEmptyString(account?.mode)

    if (
        !proposalId ||
        !confirmationToken ||
        !nonEmptyString(data.status) ||
        !side ||
        !Number.isInteger(data.quantity) ||
        !orderType ||
        !instrumentLabel ||
        !exchange ||
        !code ||
        !accountLabel ||
        !accountMode ||
        !expiresAt
    ) {
        return null
    }

    const operation = parseOrderPreviewOperation(data.operation)
    const currentSnapshot = parseOrderPreviewSnapshot(data.currentSnapshot)
    const proposedSnapshot = parseOrderPreviewSnapshot(data.proposedSnapshot)
    const targetOrderId = nonEmptyString(data.targetOrderId)

    if (operation === 'MODIFY' && (!currentSnapshot || !proposedSnapshot || !targetOrderId)) {
        return null
    }

    if (operation === 'CANCEL' && (!currentSnapshot || !targetOrderId)) {
        return null
    }

    const legs = parseOrderPreviewLegs(data.legs) ?? parseOrderPreviewLegs(data.legs, true)

    return {
        proposalId,
        status: data.status as string,
        operation,
        confirmationToken,
        side,
        quantity: data.quantity as number,
        orderType,
        limitPrice: parsePriceRupees(data.limitPrice),
        triggerPrice: parsePriceRupees(data.triggerPrice),
        product: nonEmptyString(data.product),
        validity: nonEmptyString(data.validity),
        lotSize: typeof data.lotSize === 'number' ? data.lotSize : null,
        lots: typeof data.lots === 'number' ? data.lots : null,
        icebergLegs: typeof data.icebergLegs === 'number' ? data.icebergLegs : null,
        disclosedQuantity: typeof data.disclosedQuantity === 'number' ? data.disclosedQuantity : null,
        condition: parseOrderPreviewCondition(data.condition),
        positionExit: parseOrderPreviewPositionExit(data.positionExit),
        positionProtection: parseOrderPreviewPositionProtection(data.positionProtection),
        basketLegs: parseOrderPreviewBasketLegs(data.basketLegs),
        strategy: parseOrderPreviewStrategy(data.strategy),
        exitAll: parseOrderPreviewExitAll(data.exitAll),
        cancelsConditionalBasket: data.cancelsConditionalBasket === true,
        requiredMarginRupees: parsePriceRupees(data.requiredMarginRupees),
        contractLabel: nonEmptyString(data.contractLabel),
        instrument: { label: instrumentLabel, exchange, code },
        account: { label: accountLabel, mode: accountMode },
        expiresAt,
        messages: Array.isArray(data.messages) ? data.messages.filter((message): message is string => typeof message === 'string') : [],
        orderId:
            nonEmptyString(data.orderId) ??
            (operation === 'MODIFY' || operation === 'CANCEL' ? targetOrderId : null),
        brokerOrderId: nonEmptyString(data.brokerOrderId),
        orderStatus: nonEmptyString(data.orderStatus),
        executionErrorMessage: nonEmptyString(data.executionErrorMessage),
        clientOrderId: nonEmptyString(data.clientOrderId),
        targetOrderId,
        currentSnapshot,
        proposedSnapshot: operation === 'CANCEL' ? (proposedSnapshot ?? null) : proposedSnapshot,
        legs,
        modifyLegRole: nonEmptyString(data.modifyLegRole),
    }
}

function isProposalArtifactType(type: string | undefined): boolean {
    return type === ORDER_PROPOSAL_ARTIFACT_TYPE || type === ORDER_PREVIEW_ARTIFACT_TYPE
}

function latestCustomArtifactData(
    artifacts: ConversationArtifact[] | undefined,
    matchesType: (type: string | undefined) => boolean,
): unknown | null {
    if (!Array.isArray(artifacts)) return null
    for (let index = artifacts.length - 1; index >= 0; index -= 1) {
        const artifact = artifacts[index]
        if (artifact?.kind === 'custom' && matchesType(artifact.type)) {
            return artifact.data
        }
    }
    return null
}

function customArtifactFromMetadata(
    metadata: Record<string, unknown>,
    matchesType: (type: string | undefined) => boolean,
): unknown | null {
    const messages = metadata.new_messages
    if (!Array.isArray(messages)) return null
    return latestCustomArtifactData(messages as ConversationArtifact[], matchesType)
}

export function orderPreviewFromArtifacts(artifacts: ConversationArtifact[] | undefined): OrderPreview | null {
    const data = latestCustomArtifactData(artifacts, isProposalArtifactType)
    return data ? parseOrderPreview(data) : null
}

export function orderPreviewFromMetadata(metadata: Record<string, unknown>): OrderPreview | null {
    const data = customArtifactFromMetadata(metadata, isProposalArtifactType)
    return data ? parseOrderPreview(data) : null
}

type MessageWithOrderPreview = { orderPreview?: OrderPreview }

/** Only the latest ready proposal in the transcript stays actionable (stateless token revision). */
export function markSupersededOrderPreviews<T extends MessageWithOrderPreview>(messages: T[]): T[] {
    let lastReadyIndex = -1
    messages.forEach((message, index) => {
        const preview = message.orderPreview
        if (preview && preview.status.toUpperCase() === 'READY_FOR_CONFIRMATION') {
            lastReadyIndex = index
        }
    })
    return messages.map((message, index) => {
        const preview = message.orderPreview
        if (!preview || index === lastReadyIndex) return message
        if (preview.status.toUpperCase() !== 'READY_FOR_CONFIRMATION') return message
        return {
            ...message,
            orderPreview: { ...preview, status: 'SUPERSEDED' },
        }
    })
}

export function isLiveOrderPreview(preview: OrderPreview): boolean {
    return preview.account.mode.trim().toUpperCase() === 'LIVE'
}

export function isOrderPreviewExpired(preview: OrderPreview, now = Date.now()): boolean {
    if (preview.status.toUpperCase() === 'EXPIRED') return true
    const expiry = Date.parse(preview.expiresAt)
    return Number.isFinite(expiry) && expiry <= now
}

const POST_SUBMIT_PROPOSAL_STATUSES = new Set(['SUBMITTING', 'SUBMITTED', 'EXECUTION_FAILED'])

export function resolveClientOrderIdForPreview(preview: OrderPreview): string {
    const entryLeg = preview.legs?.find((leg) => leg.role === 'ENTRY')
    if (entryLeg?.clientOrderId) return entryLeg.clientOrderId
    return preview.clientOrderId?.trim() || stableAiProposalClientOrderId(preview.proposalId)
}

export function toLinkedOmsOrder(order: {
    clientOrderId: string
    orderId?: string
    status?: number
    secondaryStatus?: number
    filledQuantity?: number
    message?: string
}): LinkedOmsOrder {
    return {
        clientOrderId: order.clientOrderId,
        orderId: order.orderId,
        status: order.status,
        secondaryStatus: order.secondaryStatus,
        filledQuantity: order.filledQuantity,
        message: order.message,
    }
}

export function findLinkedOmsOrder(
    preview: OrderPreview,
    orders: LinkedOmsOrder[] | undefined,
): LinkedOmsOrder | undefined {
    if (!orders?.length) return undefined
    const clientOrderId = resolveClientOrderIdForPreview(preview)
    const byClient = orders.find((order) => order.clientOrderId === clientOrderId)
    if (byClient) return byClient
    const targetOrderId = preview.targetOrderId?.trim()
    if (targetOrderId) {
        const byTarget = orders.find((order) => order.orderId === targetOrderId)
        if (byTarget) return byTarget
    }
    const orderId = preview.orderId?.trim()
    if (orderId) {
        return orders.find((order) => order.orderId === orderId)
    }
    return undefined
}

export function isPostSubmissionOrderPreview(preview: OrderPreview, linkedOrder?: LinkedOmsOrder): boolean {
    if (linkedOrder) return true
    return POST_SUBMIT_PROPOSAL_STATUSES.has(preview.status.toUpperCase())
}

const OMS_STATUS_NAME: Record<number, string> = {
    0: 'ORDER_STATUS_UNKNOWN',
    1: 'INACTIVE',
    2: 'PENDING',
    3: 'TRIGGER_PENDING',
    4: 'FAILED',
    6: 'OPEN',
    8: 'REJECTED',
    10: 'PARTIALLY_FILLED',
    12: 'EXECUTED',
    14: 'CANCELLED',
}

export function applyOmsOrderToPreview(preview: OrderPreview, order: LinkedOmsOrder): OrderPreview {
    const status = order.status ?? 0
    const orderId = order.orderId ?? preview.orderId ?? preview.targetOrderId
    return {
        ...preview,
        status: 'SUBMITTED',
        orderId,
        clientOrderId: order.clientOrderId || preview.clientOrderId,
        orderStatus: OMS_STATUS_NAME[status] ?? 'ORDER_STATUS_UNKNOWN',
        targetOrderId: preview.targetOrderId ?? order.orderId ?? null,
    }
}

/** Merge persisted proposal snapshot with the shared Redux order book (reload + live WS). */
export function resolveOrderPreviewWithOms(
    preview: OrderPreview,
    orders: LinkedOmsOrder[] | undefined,
): OrderPreview {
    const linked = findLinkedOmsOrder(preview, orders)
    if (!linked) return preview
    return applyOmsOrderToPreview(preview, linked)
}

export function isOmsOrderTerminalFailure(order: LinkedOmsOrder): boolean {
    const status = order.status ?? 0
    return status === 4 || status === 8
}

export function canConfirmOrderPreview(
    preview: OrderPreview,
    blocked = false,
    linkedOrder?: LinkedOmsOrder,
): boolean {
    if (blocked || linkedOrder || isPostSubmissionOrderPreview(preview)) {
        return false
    }
    return preview.status.toUpperCase() === 'READY_FOR_CONFIRMATION' && !isOrderPreviewExpired(preview)
}

export function buildOrderPreviewCardModel(
    preview: OrderPreview,
    blocked = false,
    linkedOrder?: LinkedOmsOrder,
): OrderPreviewCardModel {
    const modify = isModifyOrderPreview(preview)
    const cancel = isCancelOrderPreview(preview)
    const live = isLiveOrderPreview(preview)
    const side = preview.side.toUpperCase()
    const trackingOms = isPostSubmissionOrderPreview(preview, linkedOrder)
    const canConfirm = canConfirmOrderPreview(preview, blocked, linkedOrder)

    const omsStatus = linkedOrder?.status
    const confirmButtonLabel = cancel
        ? live
            ? 'Confirm LIVE cancellation'
            : 'Confirm cancellation'
        : modify
          ? live
              ? 'Confirm LIVE modification'
              : 'Confirm modification'
          : preview.positionProtection
            ? live
                ? 'Place LIVE stop / target'
                : 'Place stop / target'
            : preview.positionExit
            ? live
                ? 'Place LIVE exit order'
                : 'Place exit order'
            : live
              ? preview.condition
                  ? 'Place LIVE conditional order'
                  : 'Place LIVE order'
              : preview.condition
                ? 'Place conditional order'
                : 'Place order'

    const linkedOrderId = preview.orderId ?? linkedOrder?.orderId ?? preview.targetOrderId ?? undefined

    const bracket = isProtectiveStopBracketPreview(preview)
    const entryLeg = preview.legs?.find((leg) => leg.role === 'ENTRY')
    const stopLeg = preview.legs?.find((leg) => leg.role === 'PROTECTIVE_STOP')
    const targetLeg = preview.legs?.find((leg) => leg.role === 'TARGET')
    const threeLeg = isThreeLegBracketPreview(preview)

    const createFields = {
        side,
        instrumentCode: `${preview.instrument.exchange}:${preview.instrument.code}`,
        ...(bracket && entryLeg && stopLeg
            ? {
                  isProtectiveStopBracket: true,
                  reviewProtectiveStopHint: threeLeg && targetLeg
                      ? 'The protective stop and target activate after the entry fills. Confirm to place all three legs.'
                      : 'The protective stop activates after the entry fills. Confirm to place both legs.',
              }
            : {}),
        ...(!bracket &&
        (isStopMarketOrderPreview(preview) || isStopLimitOrderPreview(preview)) &&
        preview.triggerPrice
            ? { triggerPriceLabel: formatOrderPriceRupees(preview.triggerPrice) }
            : {}),
        ...(!bracket &&
        (isLimitOrderPreview(preview) || isStopLimitOrderPreview(preview)) &&
        preview.limitPrice
            ? { limitPriceLabel: formatOrderPriceRupees(preview.limitPrice) }
            : {}),
    }

    const modifyLegLabel =
        preview.modifyLegRole?.toUpperCase() === 'TARGET'
            ? 'Target'
            : preview.modifyLegRole?.toUpperCase() === 'PROTECTIVE_STOP'
              ? 'Stop trigger'
              : undefined
    const modifyFields = modify && preview.currentSnapshot && preview.proposedSnapshot
        ? {
              targetOrderIdShort: preview.targetOrderId ? shortOrderId(preview.targetOrderId) : undefined,
              modifyLegLabel,
              modifyCurrent: snapshotToModifyDisplay(preview.currentSnapshot),
              modifyProposed: snapshotToModifyDisplay(preview.proposedSnapshot),
              side: preview.proposedSnapshot.side.toUpperCase(),
              instrumentCode: `${preview.instrument.exchange}:${preview.instrument.code}`,
          }
        : {}

    const cancelLegsWithDisposition =
        cancel && preview.legs?.some((leg) => leg.cancelDisposition)
            ? preview.legs.map(cancelBracketLegDisplayFromLeg)
            : undefined
    const cancelFields =
        cancel && preview.currentSnapshot && preview.targetOrderId
            ? {
                  targetOrderIdShort: shortOrderId(preview.targetOrderId),
                  ...(cancelLegsWithDisposition
                      ? { cancelBracketLegs: cancelLegsWithDisposition }
                      : {
                            cancelOrderDetails: snapshotToModifyDisplay(preview.currentSnapshot),
                        }),
                  currentSnapshotStatus: preview.currentSnapshot.status ?? undefined,
                  side: preview.currentSnapshot.side.toUpperCase(),
                  instrumentCode: `${preview.instrument.exchange}:${preview.instrument.code}`,
                  ...(!cancelLegsWithDisposition &&
                  (normalizedOrderTypeKey(preview.currentSnapshot.orderType) === 'SL_M' ||
                      normalizedOrderTypeKey(preview.currentSnapshot.orderType) === 'SL') &&
                  preview.currentSnapshot.triggerPrice
                      ? { triggerPriceLabel: formatOrderPriceRupees(preview.currentSnapshot.triggerPrice) }
                      : {}),
                  ...(!cancelLegsWithDisposition &&
                  (normalizedOrderTypeKey(preview.currentSnapshot.orderType) === 'LIMIT' ||
                      normalizedOrderTypeKey(preview.currentSnapshot.orderType) === 'SL') &&
                  preview.currentSnapshot.limitPrice
                      ? { limitPriceLabel: formatOrderPriceRupees(preview.currentSnapshot.limitPrice) }
                      : {}),
              }
            : {}

    return {
        operation: preview.operation,
        confirmButtonLabel,
        ...((cancel ? cancelFields : modify ? modifyFields : createFields) as Pick<OrderPreviewCardModel, 'side' | 'instrumentCode'> &
            Partial<OrderPreviewCardModel>),
        accountLabel: preview.account.label,
        accountMode: preview.account.mode.toUpperCase(),
        trackingOms,
        canConfirm,
        ...(linkedOrderId ? { orderId: linkedOrderId } : {}),
        ...(omsStatus !== undefined && omsStatus !== 0 ? { omsStatus } : {}),
        ...(linkedOrder?.filledQuantity !== undefined && linkedOrder.filledQuantity > 0
            ? { filledQuantity: linkedOrder.filledQuantity }
            : {}),
        ...(linkedOrder?.message?.trim() ? { brokerMessage: linkedOrder.message.trim() } : {}),
    }
}

export function buildConfirmOrderProposalRequest(
    preview: OrderPreview,
    liveAcknowledgement?: typeof LIVE_ORDER_ACKNOWLEDGEMENT,
): ConfirmOrderProposalRequest {
    if (isLiveOrderPreview(preview) && liveAcknowledgement !== LIVE_ORDER_ACKNOWLEDGEMENT) {
        throw new Error('LIVE order proposal requires explicit acknowledgement')
    }
    return {
        confirmationToken: preview.confirmationToken,
        ...(isLiveOrderPreview(preview) ? { liveAcknowledgement } : {}),
    }
}

export function mergeExecutionIntoPreview(preview: OrderPreview, execution: OrderPreview): OrderPreview {
    const targetOrderId = execution.targetOrderId ?? preview.targetOrderId
    const cancel = isCancelOrderPreview(preview)
    return {
        ...preview,
        operation: execution.operation ?? preview.operation,
        status: execution.status,
        messages: execution.messages.length ? execution.messages : preview.messages,
        orderId: execution.orderId ?? preview.orderId ?? targetOrderId ?? null,
        brokerOrderId: execution.brokerOrderId ?? preview.brokerOrderId,
        orderStatus: execution.orderStatus ?? preview.orderStatus,
        executionErrorMessage: execution.executionErrorMessage ?? preview.executionErrorMessage,
        clientOrderId: execution.clientOrderId ?? preview.clientOrderId,
        limitPrice: cancel ? preview.limitPrice : execution.limitPrice ?? preview.limitPrice,
        triggerPrice: cancel ? preview.triggerPrice : execution.triggerPrice ?? preview.triggerPrice,
        orderType: cancel ? preview.orderType : execution.orderType || preview.orderType,
        targetOrderId,
        currentSnapshot: preview.currentSnapshot ?? execution.currentSnapshot,
        proposedSnapshot: cancel ? preview.proposedSnapshot ?? null : execution.proposedSnapshot ?? preview.proposedSnapshot,
        legs: mergeLegExecution(preview.legs, execution.legs) ?? preview.legs,
    }
}

/** Quantity shown on the card (modify proposals prefer proposed snapshot qty). */
export function displayQuantityForPreview(preview: OrderPreview): number {
    return preview.proposedSnapshot?.quantity ?? preview.currentSnapshot?.quantity ?? preview.quantity
}

export function classifyOrderPreviewError(error: unknown): OrderPreviewIssue {
    const status = error && typeof error === 'object' && 'status' in error ? Number((error as { status?: unknown }).status) : undefined
    if (status === 409) return 'stale'
    if (status === 410) return 'expired'
    return 'error'
}

export function hasSubmissionInProgress(preview: OrderPreview): boolean {
    return preview.status.toUpperCase() === 'SUBMITTING'
}

export function hasDeterministicExecutionFailure(preview: OrderPreview): boolean {
    return preview.status.toUpperCase() === 'EXECUTION_FAILED'
}

export const ORDER_INQUIRY_ARTIFACT_TYPE = 'order_inquiry'

export type OrderInquiryLeg = {
    role: OrderPreviewLegRole
    orderId: string
    clientOrderId: string
    side: string
    quantity: number
    filledQuantity: number
    remainingQuantity: number
    orderType: string
    price?: number | null
    triggerPrice?: number | null
    status: string
    statusMessage?: string | null
}

export type OrderInquiryGroup = {
    kind: 'SINGLE' | 'BRACKET' | string
    batchId?: string | null
    basketId?: string | null
    instrument: OrderPreviewInstrument
    exchange: string
    securityCode: string
    side: string
    product: string
    validity: string
    legs: OrderInquiryLeg[]
}

export type OrderInquiryMatchSummary = {
    kind: string
    instrumentLabel: string
    side: string
    quantity: number
    orderType: string
    price?: string | null
    status: string
    orderId?: string | null
}

export type OrderInquiry = {
    outcome: string
    message?: string | null
    groups: OrderInquiryGroup[]
    matchSummaries?: OrderInquiryMatchSummary[]
}

const LEG_ROLE_ALIASES: Record<string, OrderPreviewLegRole> = {
    ENTRY: 'ENTRY',
    PROTECTIVE_STOP: 'PROTECTIVE_STOP',
    TARGET: 'TARGET',
}

function parseInquiryLeg(value: unknown): OrderInquiryLeg | null {
    if (!value || typeof value !== 'object') return null
    const leg = value as Record<string, unknown>
    const roleRaw = String(leg.role || '').toUpperCase()
    const role = LEG_ROLE_ALIASES[roleRaw]
    if (!role) return null
    const orderId = String(leg.orderId || '').trim()
    const clientOrderId = String(leg.clientOrderId || '').trim()
    if (!orderId && !clientOrderId) return null
    return {
        role,
        orderId: orderId || clientOrderId,
        clientOrderId: clientOrderId || orderId,
        side: String(leg.side || '').toUpperCase(),
        quantity: Number(leg.quantity) || 0,
        filledQuantity: Number(leg.filledQuantity) || 0,
        remainingQuantity: Number(leg.remainingQuantity) || 0,
        orderType: String(leg.orderType || '').toUpperCase(),
        price: leg.price != null ? Number(leg.price) : null,
        triggerPrice: leg.triggerPrice != null ? Number(leg.triggerPrice) : null,
        status: String(leg.status || 'ORDER_STATUS_UNKNOWN').toUpperCase(),
        statusMessage: typeof leg.statusMessage === 'string' ? leg.statusMessage : null,
    }
}

export function parseOrderInquiry(value: unknown): OrderInquiry | null {
    if (!value || typeof value !== 'object') return null
    const data = value as Record<string, unknown>
    const outcome = String(data.outcome || '').toUpperCase()
    if (!outcome) return null
    const groupsRaw = data.groups
    const groups: OrderInquiryGroup[] = []
    if (Array.isArray(groupsRaw)) {
        for (const item of groupsRaw) {
            if (!item || typeof item !== 'object') continue
            const group = item as Record<string, unknown>
            const instrumentRaw = group.instrument
            if (!instrumentRaw || typeof instrumentRaw !== 'object') continue
            const instrument = instrumentRaw as Record<string, unknown>
            const legsRaw = group.legs
            const legs: OrderInquiryLeg[] = []
            if (Array.isArray(legsRaw)) {
                for (const leg of legsRaw) {
                    const parsed = parseInquiryLeg(leg)
                    if (parsed) legs.push(parsed)
                }
            }
            if (!legs.length) continue
            groups.push({
                kind: String(group.kind || 'SINGLE'),
                batchId: typeof group.batchId === 'string' ? group.batchId : null,
                basketId: typeof group.basketId === 'string' ? group.basketId : null,
                instrument: {
                    label: String(instrument.label || instrument.code || ''),
                    exchange: String(instrument.exchange || group.exchange || ''),
                    code: String(instrument.code || ''),
                },
                exchange: String(group.exchange || instrument.exchange || ''),
                securityCode: String(group.securityCode || instrument.code || ''),
                side: String(group.side || legs[0]?.side || ''),
                product: String(group.product || ''),
                validity: String(group.validity || ''),
                legs,
            })
        }
    }
    const summariesRaw = data.matchSummaries
    const matchSummaries: OrderInquiryMatchSummary[] = []
    if (Array.isArray(summariesRaw)) {
        for (const item of summariesRaw) {
            if (!item || typeof item !== 'object') continue
            const summary = item as Record<string, unknown>
            matchSummaries.push({
                kind: String(summary.kind || ''),
                instrumentLabel: String(summary.instrumentLabel || ''),
                side: String(summary.side || ''),
                quantity: Number(summary.quantity) || 0,
                orderType: String(summary.orderType || ''),
                price: typeof summary.price === 'string' ? summary.price : null,
                status: String(summary.status || ''),
                orderId: typeof summary.orderId === 'string' ? summary.orderId : null,
            })
        }
    }
    return {
        outcome,
        message: typeof data.message === 'string' ? data.message : null,
        groups,
        matchSummaries: matchSummaries.length ? matchSummaries : undefined,
    }
}

export function orderInquiryFromArtifacts(artifacts: ConversationArtifact[] | undefined): OrderInquiry | null {
    const data = latestCustomArtifactData(artifacts, (type) => type === ORDER_INQUIRY_ARTIFACT_TYPE)
    return data ? parseOrderInquiry(data) : null
}

export function orderInquiryFromMetadata(metadata: Record<string, unknown>): OrderInquiry | null {
    const data = customArtifactFromMetadata(metadata, (type) => type === ORDER_INQUIRY_ARTIFACT_TYPE)
    return data ? parseOrderInquiry(data) : null
}

export const POSITION_INQUIRY_ARTIFACT_TYPE = 'position_inquiry'
export const ACCOUNT_FUNDS_INQUIRY_ARTIFACT_TYPE = 'account_funds_inquiry'
export const HOLDINGS_INQUIRY_ARTIFACT_TYPE = 'holdings_inquiry'
export const PNL_INQUIRY_ARTIFACT_TYPE = 'pnl_inquiry'
export const TRADE_HISTORY_ARTIFACT_TYPE = 'trade_history'

export type PositionInquirySnapshot = {
    instrument: OrderPreviewInstrument
    exchange: string
    securityCode: string
    product: string
    netQuantity: number
    side: string
    averagePrice?: number | null
    dayBuyQuantity?: number | null
    daySellQuantity?: number | null
    realizedPnl?: number | null
}

export type PositionInquiry = {
    outcome: string
    message?: string | null
    accountLabel?: string | null
    executionMode?: string | null
    positions: PositionInquirySnapshot[]
}

export type HoldingSnapshot = {
    instrument: OrderPreviewInstrument
    exchange: string
    securityCode: string
    quantity: number
    /** Bought today, settling into the demat account tomorrow. */
    t1Quantity?: number | null
    averageBuyPrice?: number | null
    investedValue?: number | null
}

export type HoldingsInquiry = {
    outcome: string
    message?: string | null
    accountLabel?: string | null
    executionMode?: string | null
    holdings: HoldingSnapshot[]
    totalInvestedValue?: number | null
}

export function parseHoldingsInquiry(value: unknown): HoldingsInquiry | null {
    if (!value || typeof value !== 'object') return null
    const data = value as Record<string, unknown>
    const outcome = String(data.outcome || '').toUpperCase()
    if (!outcome) return null
    const holdings: HoldingSnapshot[] = []
    if (Array.isArray(data.holdings)) {
        for (const item of data.holdings) {
            if (!item || typeof item !== 'object') continue
            const row = item as Record<string, unknown>
            const instrumentRaw = row.instrument
            if (!instrumentRaw || typeof instrumentRaw !== 'object') continue
            const instrument = instrumentRaw as Record<string, unknown>
            holdings.push({
                instrument: {
                    label: String(instrument.label || ''),
                    exchange: String(instrument.exchange || ''),
                    code: String(instrument.code || ''),
                },
                exchange: String(row.exchange || ''),
                securityCode: String(row.securityCode || ''),
                quantity: Number(row.quantity) || 0,
                t1Quantity: row.t1Quantity != null ? Number(row.t1Quantity) : null,
                averageBuyPrice: parsePriceRupees(row.averageBuyPrice),
                investedValue: parsePriceRupees(row.investedValue),
            })
        }
    }
    return {
        outcome,
        message: typeof data.message === 'string' ? data.message : null,
        accountLabel: nonEmptyString(data.accountLabel),
        executionMode: nonEmptyString(data.executionMode),
        holdings,
        totalInvestedValue: parsePriceRupees(data.totalInvestedValue),
    }
}

export function holdingsInquiryFromArtifacts(artifacts: ConversationArtifact[] | undefined): HoldingsInquiry | null {
    const data = latestCustomArtifactData(artifacts, (type) => type === HOLDINGS_INQUIRY_ARTIFACT_TYPE)
    return data ? parseHoldingsInquiry(data) : null
}

export function holdingsInquiryFromMetadata(metadata: Record<string, unknown>): HoldingsInquiry | null {
    const data = customArtifactFromMetadata(metadata, (type) => type === HOLDINGS_INQUIRY_ARTIFACT_TYPE)
    return data ? parseHoldingsInquiry(data) : null
}

export type PnlRow = {
    instrument: OrderPreviewInstrument
    exchange: string
    securityCode: string
    product: string
    netQuantity: number
    side: string
    averagePrice?: number | null
    lastPrice?: number | null
    realisedPnl?: number | null
    unrealisedPnl?: number | null
}

export type PnlInquiry = {
    outcome: string
    message?: string | null
    rows: PnlRow[]
    totalRealisedPnl?: number | null
    totalUnrealisedPnl?: number | null
    totalPnl?: number | null
    /** Some open position had no live price, so unrealised P&L is incomplete. */
    livePriceMissing: boolean
}

export type TradeFill = {
    instrumentLabel: string
    exchange: string
    securityCode: string
    side: string
    quantity: number
    price?: number | null
    tradedAt?: string | null
}

export type TradeHistory = {
    outcome: string
    message?: string | null
    trades: TradeFill[]
    totalCount: number
}

export function parsePnlInquiry(value: unknown): PnlInquiry | null {
    if (!value || typeof value !== 'object') return null
    const data = value as Record<string, unknown>
    const outcome = String(data.outcome || '').toUpperCase()
    if (!outcome) return null
    const rows: PnlRow[] = []
    if (Array.isArray(data.rows)) {
        for (const item of data.rows) {
            if (!item || typeof item !== 'object') continue
            const row = item as Record<string, unknown>
            const instrumentRaw = row.instrument
            if (!instrumentRaw || typeof instrumentRaw !== 'object') continue
            const instrument = instrumentRaw as Record<string, unknown>
            rows.push({
                instrument: {
                    label: String(instrument.label || ''),
                    exchange: String(instrument.exchange || ''),
                    code: String(instrument.code || ''),
                },
                exchange: String(row.exchange || ''),
                securityCode: String(row.securityCode || ''),
                product: String(row.product || ''),
                netQuantity: Number(row.netQuantity) || 0,
                side: String(row.side || '').toUpperCase(),
                averagePrice: parsePriceRupees(row.averagePrice),
                lastPrice: parsePriceRupees(row.lastPrice),
                realisedPnl: row.realisedPnl != null ? Number(row.realisedPnl) : null,
                unrealisedPnl: row.unrealisedPnl != null ? Number(row.unrealisedPnl) : null,
            })
        }
    }
    return {
        outcome,
        message: typeof data.message === 'string' ? data.message : null,
        rows,
        totalRealisedPnl: data.totalRealisedPnl != null ? Number(data.totalRealisedPnl) : null,
        totalUnrealisedPnl: data.totalUnrealisedPnl != null ? Number(data.totalUnrealisedPnl) : null,
        totalPnl: data.totalPnl != null ? Number(data.totalPnl) : null,
        livePriceMissing: data.livePriceMissing === true,
    }
}

export function parseTradeHistory(value: unknown): TradeHistory | null {
    if (!value || typeof value !== 'object') return null
    const data = value as Record<string, unknown>
    const outcome = String(data.outcome || '').toUpperCase()
    if (!outcome) return null
    const trades: TradeFill[] = []
    if (Array.isArray(data.trades)) {
        for (const item of data.trades) {
            if (!item || typeof item !== 'object') continue
            const row = item as Record<string, unknown>
            trades.push({
                instrumentLabel: String(row.instrumentLabel || ''),
                exchange: String(row.exchange || ''),
                securityCode: String(row.securityCode || ''),
                side: String(row.side || '').toUpperCase(),
                quantity: Number(row.quantity) || 0,
                price: parsePriceRupees(row.price),
                tradedAt: nonEmptyString(row.tradedAt),
            })
        }
    }
    return {
        outcome,
        message: typeof data.message === 'string' ? data.message : null,
        trades,
        totalCount: Number(data.totalCount) || trades.length,
    }
}

export function pnlInquiryFromArtifacts(artifacts: ConversationArtifact[] | undefined): PnlInquiry | null {
    const data = latestCustomArtifactData(artifacts, (type) => type === PNL_INQUIRY_ARTIFACT_TYPE)
    return data ? parsePnlInquiry(data) : null
}

export function pnlInquiryFromMetadata(metadata: Record<string, unknown>): PnlInquiry | null {
    const data = customArtifactFromMetadata(metadata, (type) => type === PNL_INQUIRY_ARTIFACT_TYPE)
    return data ? parsePnlInquiry(data) : null
}

export function tradeHistoryFromArtifacts(artifacts: ConversationArtifact[] | undefined): TradeHistory | null {
    const data = latestCustomArtifactData(artifacts, (type) => type === TRADE_HISTORY_ARTIFACT_TYPE)
    return data ? parseTradeHistory(data) : null
}

export function tradeHistoryFromMetadata(metadata: Record<string, unknown>): TradeHistory | null {
    const data = customArtifactFromMetadata(metadata, (type) => type === TRADE_HISTORY_ARTIFACT_TYPE)
    return data ? parseTradeHistory(data) : null
}

export type AccountFundsInquiry = {
    outcome: string
    message?: string | null
    account?: {
        accountLabel: string
        executionMode: string
        marginAvailable?: number | null
        marginUsed?: number | null
        cashAvailable?: number | null
    } | null
}

export function parsePositionInquiry(value: unknown): PositionInquiry | null {
    if (!value || typeof value !== 'object') return null
    const data = value as Record<string, unknown>
    const outcome = String(data.outcome || '').toUpperCase()
    if (!outcome) return null
    const positions: PositionInquirySnapshot[] = []
    const positionsRaw = data.positions
    if (Array.isArray(positionsRaw)) {
        for (const item of positionsRaw) {
            if (!item || typeof item !== 'object') continue
            const row = item as Record<string, unknown>
            const instrumentRaw = row.instrument
            if (!instrumentRaw || typeof instrumentRaw !== 'object') continue
            const instrument = instrumentRaw as Record<string, unknown>
            positions.push({
                instrument: {
                    label: String(instrument.label || ''),
                    exchange: String(instrument.exchange || ''),
                    code: String(instrument.code || ''),
                },
                exchange: String(row.exchange || ''),
                securityCode: String(row.securityCode || ''),
                product: String(row.product || ''),
                netQuantity: Number(row.netQuantity) || 0,
                side: String(row.side || '').toUpperCase(),
                averagePrice: row.averagePrice != null ? Number(row.averagePrice) : null,
                dayBuyQuantity: row.dayBuyQuantity != null ? Number(row.dayBuyQuantity) : null,
                daySellQuantity: row.daySellQuantity != null ? Number(row.daySellQuantity) : null,
                realizedPnl: row.realizedPnl != null ? Number(row.realizedPnl) : null,
            })
        }
    }
    return {
        outcome,
        message: typeof data.message === 'string' ? data.message : null,
        accountLabel: typeof data.accountLabel === 'string' ? data.accountLabel : null,
        executionMode: typeof data.executionMode === 'string' ? data.executionMode : null,
        positions,
    }
}

export function parseAccountFundsInquiry(value: unknown): AccountFundsInquiry | null {
    if (!value || typeof value !== 'object') return null
    const data = value as Record<string, unknown>
    const outcome = String(data.outcome || '').toUpperCase()
    if (!outcome) return null
    let account: AccountFundsInquiry['account'] = null
    const accountRaw = data.account
    if (accountRaw && typeof accountRaw === 'object') {
        const row = accountRaw as Record<string, unknown>
        account = {
            accountLabel: String(row.accountLabel || 'Trading account'),
            executionMode: String(row.executionMode || ''),
            marginAvailable: row.marginAvailable != null ? Number(row.marginAvailable) : null,
            marginUsed: row.marginUsed != null ? Number(row.marginUsed) : null,
            cashAvailable: row.cashAvailable != null ? Number(row.cashAvailable) : null,
        }
    }
    return {
        outcome,
        message: typeof data.message === 'string' ? data.message : null,
        account,
    }
}

export function positionInquiryFromArtifacts(artifacts: ConversationArtifact[] | undefined): PositionInquiry | null {
    const data = latestCustomArtifactData(artifacts, (type) => type === POSITION_INQUIRY_ARTIFACT_TYPE)
    return data ? parsePositionInquiry(data) : null
}

export function accountFundsInquiryFromArtifacts(
    artifacts: ConversationArtifact[] | undefined,
): AccountFundsInquiry | null {
    const data = latestCustomArtifactData(artifacts, (type) => type === ACCOUNT_FUNDS_INQUIRY_ARTIFACT_TYPE)
    return data ? parseAccountFundsInquiry(data) : null
}

export function positionInquiryFromMetadata(metadata: Record<string, unknown>): PositionInquiry | null {
    const data = customArtifactFromMetadata(metadata, (type) => type === POSITION_INQUIRY_ARTIFACT_TYPE)
    return data ? parsePositionInquiry(data) : null
}

export function accountFundsInquiryFromMetadata(metadata: Record<string, unknown>): AccountFundsInquiry | null {
    const data = customArtifactFromMetadata(metadata, (type) => type === ACCOUNT_FUNDS_INQUIRY_ARTIFACT_TYPE)
    return data ? parseAccountFundsInquiry(data) : null
}

export type TradingTurnArtifactPatch = {
    orderPreview?: OrderPreview
    orderInquiry?: OrderInquiry
    positionInquiry?: PositionInquiry
    holdingsInquiry?: HoldingsInquiry
    pnlInquiry?: PnlInquiry
    tradeHistory?: TradeHistory
    accountFundsInquiry?: AccountFundsInquiry
}

/** Parse custom trading artifacts from worker `new_messages` on streaming or completed events. */
export function tradingTurnArtifactPatchFromMetadata(
    metadata: Record<string, unknown>,
): TradingTurnArtifactPatch | null {
    const orderPreview = orderPreviewFromMetadata(metadata)
    const orderInquiry = orderInquiryFromMetadata(metadata)
    const positionInquiry = positionInquiryFromMetadata(metadata)
    const holdingsInquiry = holdingsInquiryFromMetadata(metadata)
    const pnlInquiry = pnlInquiryFromMetadata(metadata)
    const tradeHistory = tradeHistoryFromMetadata(metadata)
    const accountFundsInquiry = accountFundsInquiryFromMetadata(metadata)
    if (
        !orderPreview &&
        !orderInquiry &&
        !positionInquiry &&
        !holdingsInquiry &&
        !pnlInquiry &&
        !tradeHistory &&
        !accountFundsInquiry
    ) {
        return null
    }
    return {
        ...(orderPreview ? { orderPreview } : {}),
        ...(orderInquiry ? { orderInquiry } : {}),
        ...(positionInquiry ? { positionInquiry } : {}),
        ...(holdingsInquiry ? { holdingsInquiry } : {}),
        ...(pnlInquiry ? { pnlInquiry } : {}),
        ...(tradeHistory ? { tradeHistory } : {}),
        ...(accountFundsInquiry ? { accountFundsInquiry } : {}),
    }
}

export function formatInquiryRupee(value: number | null | undefined): string | null {
    if (value == null || Number.isNaN(value)) return null
    return `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function inquiryLegStatusFromOms(status: number | undefined): string | null {
    if (status == null) return null
    return OMS_STATUS_NAME[status] ?? null
}

export function applyOmsOrderToInquiryLeg(leg: OrderInquiryLeg, order: LinkedOmsOrder): OrderInquiryLeg {
    const statusName = inquiryLegStatusFromOms(order.status)
    return {
        ...leg,
        orderId: order.orderId || leg.orderId,
        clientOrderId: order.clientOrderId || leg.clientOrderId,
        status: statusName || leg.status,
        filledQuantity: order.filledQuantity ?? leg.filledQuantity,
        remainingQuantity:
            leg.quantity - (order.filledQuantity ?? leg.filledQuantity) >= 0
                ? leg.quantity - (order.filledQuantity ?? leg.filledQuantity)
                : leg.remainingQuantity,
    }
}

export function resolveOrderInquiryWithOms(inquiry: OrderInquiry, orders: LinkedOmsOrder[] | undefined): OrderInquiry {
    if (!orders?.length || !inquiry.groups.length) return inquiry
    const groups = inquiry.groups.map((group) => ({
        ...group,
        legs: group.legs.map((leg) => {
            const linked =
                orders.find((o) => o.orderId && o.orderId === leg.orderId) ||
                orders.find((o) => o.clientOrderId === leg.clientOrderId)
            return linked ? applyOmsOrderToInquiryLeg(leg, linked) : leg
        }),
    }))
    return { ...inquiry, groups }
}

export function formatInquiryLegPrice(leg: OrderInquiryLeg): string | null {
    if (leg.price != null) return formatOrderPriceRupees(leg.price)
    if (leg.triggerPrice != null) return `Trigger ${formatOrderPriceRupees(leg.triggerPrice)}`
    return null
}

export function inquiryLegTitle(role: OrderPreviewLegRole): string {
    if (role === 'PROTECTIVE_STOP') return 'Protective stop'
    if (role === 'TARGET') return 'Target'
    return 'Entry'
}

const INQUIRY_STATUS_TO_ORDER_STATUS: Record<string, OrderStatus> = {
    ORDER_STATUS_UNKNOWN: OrderStatus.ORDER_STATUS_UNKNOWN,
    UNKNOWN: OrderStatus.ORDER_STATUS_UNKNOWN,
    INACTIVE: OrderStatus.INACTIVE,
    ORDER_STATUS_INACTIVE: OrderStatus.INACTIVE,
    PENDING: OrderStatus.PENDING,
    ORDER_STATUS_PENDING: OrderStatus.PENDING,
    TRIGGER_PENDING: OrderStatus.TRIGGER_PENDING,
    ORDER_STATUS_TRIGGER_PENDING: OrderStatus.TRIGGER_PENDING,
    FAILED: OrderStatus.FAILED,
    ORDER_STATUS_FAILED: OrderStatus.FAILED,
    OPEN: OrderStatus.OPEN,
    ORDER_STATUS_OPEN: OrderStatus.OPEN,
    REJECTED: OrderStatus.REJECTED,
    ORDER_STATUS_REJECTED: OrderStatus.REJECTED,
    PARTIALLY_FILLED: OrderStatus.PARTIALLY_FILLED,
    ORDER_STATUS_PARTIALLY_FILLED: OrderStatus.PARTIALLY_FILLED,
    EXECUTED: OrderStatus.EXECUTED,
    ORDER_STATUS_EXECUTED: OrderStatus.EXECUTED,
    CANCELLED: OrderStatus.CANCELLED,
    ORDER_STATUS_CANCELLED: OrderStatus.CANCELLED,
}

export function inquiryStatusToOrderStatus(status: string): OrderStatus | undefined {
    const key = status.trim().toUpperCase()
    return INQUIRY_STATUS_TO_ORDER_STATUS[key]
}

/** Matches Java {@code UUID.nameUUIDFromBytes} used by Scorpius {@code OrderCompiler.stableClientOrderId}. */
export function stableAiProposalClientOrderId(proposalId: string): string {
    const bytes = new TextEncoder().encode(`ai-proposal:${proposalId}`)
    return formatUuidFromMd5(md5Bytes(bytes))
}

function md5Bytes(message: Uint8Array): Uint8Array {
    const K = new Int32Array([
        0xd76aa478, 0xe8c7b756, 0x242070db, 0xc1bdceee, 0xf57c0faf, 0x4787c62a, 0xa8304613, 0xfd469501,
        0x698098d8, 0x8b44f7af, 0xffff5bb1, 0x895cd7be, 0x6b901122, 0xfd987193, 0xa679438e, 0x49b40821,
        0xf61e2562, 0xc040b340, 0x265e5a51, 0xe9b6c7aa, 0xd62f105d, 0x02441453, 0xd8a1e681, 0xe7d3fbc8,
        0x21e1cde6, 0xc33707d6, 0xf4d50d87, 0x455a14ed, 0xa9e3e905, 0xfcefa3f8, 0x676f02d9, 0x8d2a4c8a,
        0xfffa3942, 0x8771f681, 0x6d9d6122, 0xfde5380c, 0xa4beea44, 0x4bdecfa9, 0xf6bb4b60, 0xbebfbc70,
        0x289b7ec6, 0xeaa127fa, 0xd4ef3085, 0x04881d05, 0xd9d4d039, 0xe6db99e5, 0x1fa27cf8, 0xc4ac5665,
        0xf4292244, 0x432aff97, 0xab9423a7, 0xfc93a039, 0x655b59c3, 0x8f0ccc92, 0xffeff47d, 0x85845dd1,
        0x6fa87e4f, 0xfe2ce6e0, 0xa3014314, 0x4e0811a1, 0xf7537e82, 0xbd3af235, 0x2ad7d2bb, 0xeb86d391,
    ])
    const S = new Int32Array([
        7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20,
        5, 9, 14, 20, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 6, 10, 15, 21, 6, 10, 15, 21,
        6, 10, 15, 21, 6, 10, 15, 21,
    ])

    const originalLength = message.length
    const withPadding = new Uint8Array(((originalLength + 9 + 63) >> 6) << 6)
    withPadding.set(message)
    withPadding[originalLength] = 0x80
    const bitLength = originalLength * 8
    const view = new DataView(withPadding.buffer)
    view.setUint32(withPadding.length - 8, bitLength >>> 0, true)
    view.setUint32(withPadding.length - 4, Math.floor(bitLength / 0x1_0000_0000), true)

    let a0 = 0x67452301
    let b0 = 0xefcdab89
    let c0 = 0x98badcfe
    let d0 = 0x10325476

    for (let offset = 0; offset < withPadding.length; offset += 64) {
        const M = new Int32Array(16)
        for (let i = 0; i < 16; i += 1) {
            M[i] = view.getInt32(offset + i * 4, true)
        }
        let a = a0
        let b = b0
        let c = c0
        let d = d0
        for (let i = 0; i < 64; i += 1) {
            let f: number
            let g: number
            if (i < 16) {
                f = (b & c) | (~b & d)
                g = i
            } else if (i < 32) {
                f = (d & b) | (~d & c)
                g = (5 * i + 1) % 16
            } else if (i < 48) {
                f = b ^ c ^ d
                g = (3 * i + 5) % 16
            } else {
                f = c ^ (b | ~d)
                g = (7 * i) % 16
            }
            const temp = d
            d = c
            c = b
            const sum = (a + f + K[i] + M[g]) | 0
            b = (b + leftRotate(sum, S[i])) | 0
            a = temp
        }
        a0 = (a0 + a) | 0
        b0 = (b0 + b) | 0
        c0 = (c0 + c) | 0
        d0 = (d0 + d) | 0
    }

    const out = new Uint8Array(16)
    const outView = new DataView(out.buffer)
    outView.setInt32(0, a0, true)
    outView.setInt32(4, b0, true)
    outView.setInt32(8, c0, true)
    outView.setInt32(12, d0, true)
    return out
}

function leftRotate(value: number, amount: number): number {
    return (value << amount) | (value >>> (32 - amount))
}

function formatUuidFromMd5(md5: Uint8Array): string {
    const bytes = new Uint8Array(md5)
    bytes[6] = (bytes[6] & 0x0f) | 0x30
    bytes[8] = (bytes[8] & 0x3f) | 0x80
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/** "Nifty 50 (underlying)" when a condition watches the underlying rather than the order's contract. */
export function formatConditionWatchLine(condition: OrderPreviewCondition): string | null {
    if (condition.watch !== 'UNDERLYING') return null
    return `${condition.watchedLabel ?? 'The underlying'} (underlying)`
}

/** A basket held behind one condition: Draco places its legs only when the condition fires. */
export function isConditionalBasketPreview(preview: { basketLegs?: unknown[] | null; condition?: unknown }): boolean {
    return Boolean(preview.basketLegs?.length && preview.condition)
}
